import { apiRequest } from "./api";

export type WalletTxnStatus = "ACTIVE" | "VOIDED";
export type EncashmentStatus = "PENDING" | "APPROVED" | "RELEASED" | "REJECTED";
export type WalletAdvanceStatus = "PENDING" | "APPROVED" | "RELEASED" | "PAID" | "REJECTED";
export type ServiceRequestStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface PaginatedResponse<T> {
  items: T[];
  nextKey: string | null;
}

export interface WalletTransaction {
  txnId: string;
  userId: string;
  businessId: string;
  storeId: string;
  recordId: string;
  serviceId?: string;
  serviceName: string;
  commissionRate: number;
  commissionAmount: number;
  price: number;
  staffName: string;
  date: string;
  status: WalletTxnStatus;
  createdAt: string;
  updatedAt: string;
}

export interface WalletEncashment {
  encashId: string;
  userId: string;
  businessId: string;
  employeeName?: string;
  amount: number;
  status: EncashmentStatus;
  requestedAt: string;
  approvedBy?: string;
  approvedAt?: string;
  releasedBy?: string;
  releasedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  comments?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WalletBalance {
  totalEarned: number;
  totalCredits: number;
  pendingEncashment: number;
  approvedEncashment: number;
  releasedEncashment: number;
  outstandingWalletAdvances: number;
  availableBalance: number;
}

export interface WalletCredit {
  creditId: string;
  userId: string;
  businessId: string;
  employeeName?: string;
  amount: number;
  reason: string;
  addedBy: string;
  addedAt: string;
  createdAt: string;
}

export interface WalletAdvance {
  advId: string;
  userId: string;
  businessId: string;
  employeeName?: string;
  amount: number;
  remainingAmount: number;
  status: WalletAdvanceStatus;
  requestedAt: string;
  approvedBy?: string;
  approvedAt?: string;
  releasedBy?: string;
  releasedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  comments?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MyWalletResponse {
  balance: WalletBalance;
  walletAdvanceLimit: number | null;
}

export interface ServiceRequest {
  requestId: string;
  userId: string;
  businessId: string;
  employeeName?: string;
  serviceId?: string;
  description: string;
  notes?: string;
  amount: number;
  commissionAmount?: number;
  status: ServiceRequestStatus;
  reviewNotes?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  walletTxnId?: string;
  createdAt: string;
}

export const walletApi = {
  // Employee
  getMyWallet: (businessId: string) =>
    apiRequest<MyWalletResponse>("/me/wallet", { params: { businessId } }),

  listMyTransactions: (businessId: string, opts?: { limit?: number; lastKey?: string }) =>
    apiRequest<PaginatedResponse<WalletTransaction>>("/me/wallet/transactions", {
      params: {
        businessId,
        limit: String(opts?.limit ?? 20),
        ...(opts?.lastKey ? { lastKey: opts.lastKey } : {}),
      },
    }),

  listMyEncashments: (businessId: string, opts?: { limit?: number; lastKey?: string }) =>
    apiRequest<PaginatedResponse<WalletEncashment>>("/me/wallet/encashments", {
      params: {
        businessId,
        limit: String(opts?.limit ?? 20),
        ...(opts?.lastKey ? { lastKey: opts.lastKey } : {}),
      },
    }),

  requestEncashment: (payload: { businessId: string; amount: number; employeeName?: string }) =>
    apiRequest<WalletEncashment>("/me/wallet/encash", { method: "POST", body: payload }),

  // Admin
  listEncashments: (businessId: string, status: EncashmentStatus = "PENDING", opts?: { limit?: number; lastKey?: string }) =>
    apiRequest<PaginatedResponse<WalletEncashment>>("/admin/wallet/encashments", {
      params: {
        businessId,
        status,
        limit: String(opts?.limit ?? 20),
        ...(opts?.lastKey ? { lastKey: opts.lastKey } : {}),
      },
    }),

  listTransactions: (businessId: string, opts?: { prefix?: string; limit?: number; lastKey?: string }) =>
    apiRequest<PaginatedResponse<WalletTransaction>>("/admin/wallet/transactions", {
      params: {
        businessId,
        limit: String(opts?.limit ?? 20),
        ...(opts?.prefix ? { prefix: opts.prefix } : {}),
        ...(opts?.lastKey ? { lastKey: opts.lastKey } : {}),
      },
    }),

  approveEncashment: (
    encashId: string,
    payload: { userId: string; businessId: string; comments?: string },
  ) =>
    apiRequest<{ success: boolean }>(`/admin/wallet/encashments/${encashId}/approve`, {
      method: "POST",
      body: payload,
    }),

  releaseEncashment: (
    encashId: string,
    payload: { userId: string; businessId: string; releasedAt: string; comments?: string },
  ) =>
    apiRequest<{ success: boolean }>(`/admin/wallet/encashments/${encashId}/release`, {
      method: "POST",
      body: payload,
    }),

  rejectEncashment: (
    encashId: string,
    payload: { userId: string; businessId: string; comments?: string },
  ) =>
    apiRequest<{ success: boolean }>(`/admin/wallet/encashments/${encashId}/reject`, {
      method: "POST",
      body: payload,
    }),

  // Wallet Advances — employee
  requestWalletAdvance: (payload: { businessId: string; amount: number; employeeName?: string }) =>
    apiRequest<WalletAdvance>("/me/wallet/advances", { method: "POST", body: payload }),

  listMyWalletAdvances: (businessId: string) =>
    apiRequest<{ items: WalletAdvance[] }>("/me/wallet/advances", { params: { businessId } }),

  // Wallet Advances — admin
  listAdminWalletAdvances: (businessId: string, status: WalletAdvanceStatus = "PENDING", opts?: { limit?: number; lastKey?: string }) =>
    apiRequest<PaginatedResponse<WalletAdvance>>("/admin/wallet/advances", {
      params: {
        businessId,
        status,
        limit: String(opts?.limit ?? 20),
        ...(opts?.lastKey ? { lastKey: opts.lastKey } : {}),
      },
    }),

  approveWalletAdvance: (advId: string, payload: { userId: string; businessId: string; comments?: string }) =>
    apiRequest<{ success: boolean }>(`/admin/wallet/advances/${advId}/approve`, { method: "POST", body: payload }),

  releaseWalletAdvance: (advId: string, payload: { userId: string; businessId: string; releasedAt: string; comments?: string }) =>
    apiRequest<{ success: boolean }>(`/admin/wallet/advances/${advId}/release`, { method: "POST", body: payload }),

  rejectWalletAdvance: (advId: string, payload: { userId: string; businessId: string; comments?: string }) =>
    apiRequest<{ success: boolean }>(`/admin/wallet/advances/${advId}/reject`, { method: "POST", body: payload }),

  markWalletAdvancePaid: (advId: string, payload: { userId: string; businessId: string }) =>
    apiRequest<{ success: boolean }>(`/admin/wallet/advances/${advId}/paid`, { method: "POST", body: payload }),

  // Wallet advance config
  getWalletAdvanceConfig: (businessId: string) =>
    apiRequest<{ walletAdvanceLimit: number | null }>("/admin/wallet/config", { params: { businessId } }),

  setWalletAdvanceLimit: (payload: { businessId: string; walletAdvanceLimit: number }) =>
    apiRequest<{ success: boolean }>("/admin/wallet/config", { method: "PATCH", body: payload }),

  // Wallet Credits — admin
  addWalletCredit: (businessId: string, payload: { userId: string; businessId: string; amount: number; reason: string }) =>
    apiRequest<WalletCredit>(`/biz/${businessId}/wallet/credit`, { method: "POST", body: payload }),

  listAdminWalletCredits: (businessId: string, opts?: { limit?: number; lastKey?: string }) =>
    apiRequest<PaginatedResponse<WalletCredit>>(`/biz/${businessId}/wallet/credits`, {
      params: {
        limit: String(opts?.limit ?? 50),
        ...(opts?.lastKey ? { lastKey: opts.lastKey } : {}),
      },
    }),

  // Service Requests — employee
  requestService: (payload: { businessId: string; serviceId?: string; description: string; notes?: string; amount: number }) =>
    apiRequest<ServiceRequest>("/me/service-requests", { method: "POST", body: payload }),

  listMyServiceRequests: (businessId: string) =>
    apiRequest<{ items: ServiceRequest[] }>("/me/service-requests", { params: { businessId } }),

  // Service Requests — admin
  adminListServiceRequests: (businessId: string, status: ServiceRequestStatus = "PENDING") =>
    apiRequest<{ items: ServiceRequest[] }>("/admin/service-requests", { params: { businessId, status } }),

  voidServiceRequest: (requestId: string, payload: { userId: string; businessId: string }) =>
    apiRequest<{ success: boolean; encashmentsCancelled: number }>(`/admin/service-requests/${requestId}/void`, { method: "POST", body: payload }),

  approveServiceRequest: (requestId: string, payload: { userId: string; businessId: string; commissionAmount: number; reviewNotes?: string }) =>
    apiRequest<{ success: boolean }>(`/admin/service-requests/${requestId}/approve`, { method: "POST", body: payload }),

  rejectServiceRequest: (requestId: string, payload: { userId: string; businessId: string; reviewNotes?: string }) =>
    apiRequest<{ success: boolean }>(`/admin/service-requests/${requestId}/reject`, { method: "POST", body: payload }),

  // Per-employee statement
  getEmployeeStatement: (businessId: string, userId: string) =>
    apiRequest<{
      balance: WalletBalance;
      transactions: WalletTransaction[];
      encashments: WalletEncashment[];
      advances: WalletAdvance[];
      credits: WalletCredit[];
    }>(`/biz/${businessId}/wallet/staff/${userId}/statement`),
};
